import type React from "react";
import { useEffect } from "react";
import { deepEqual } from "fast-equals";
import type { Location } from "history";

import useWindowSize from "@/base/hooks/useWindowSize";
import { SECTIONS_SELECTOR_HEIGHT } from "@/pages/HomePage/components/ProductList";
import type { Values } from "@/store/ScrollProvider";
import { useScrollContext } from "@/store/useScrollContext";
import { getBrandTheme } from "@/theme";

const useWindowScroll = (
  location: Location,
  scrollPositionRef: React.MutableRefObject<number>,
  isProductPage: boolean | undefined,
  getTopLen: () => number,
  setRefScrollPosition: (top: number) => void,
): Values => {
  const theme = getBrandTheme();
  const [{ values }, { setIsScrolledAboveFold, setValues }] =
    useScrollContext();
  const { isMdAndUp } = useWindowSize();

  useEffect(() => {
    const rootComponent = document.querySelector("#root");

    const onWindowScrolled = () => {
      const { top = 0 } = rootComponent?.getBoundingClientRect() || {};

      const topLen = getTopLen();

      const hasScrolledToAboveFold = top ? top > topLen : true;
      setIsScrolledAboveFold(hasScrolledToAboveFold);

      const sectionContainer = document.querySelector("#section-selectors-bar");
      const isScrollingUpward = top ? scrollPositionRef.current < top : false;

      const isStick = sectionContainer
        ? sectionContainer.classList.value.includes("show")
        : false;

      setIsScrolledAboveFold(hasScrolledToAboveFold);

      const shouldShowNavbar = Boolean(
        isScrollingUpward || hasScrolledToAboveFold || isProductPage,
      );

      let sectionLineTop: number;

      if (shouldShowNavbar) {
        sectionLineTop = hasScrolledToAboveFold
          ? 36
          : 37 + theme.config.mobileNavbarHeight;
      } else {
        sectionLineTop = 36;
      }

      const newValues = {
        sectionContainerTop: shouldShowNavbar
          ? isMdAndUp
            ? theme.config.desktopNavbarHeight
            : theme.config.mobileNavbarHeight
          : 0,
        fixedSubItemTop: shouldShowNavbar
          ? SECTIONS_SELECTOR_HEIGHT +
            (isMdAndUp
              ? theme.config.desktopNavbarHeight
              : theme.config.mobileNavbarHeight)
          : SECTIONS_SELECTOR_HEIGHT,
        sectionLineTop,
        shouldShowNavbar: shouldShowNavbar,
        transition: isStick,
      };

      setValues((prevValue) => {
        return deepEqual(prevValue, newValues) ? prevValue : newValues;
      });

      setRefScrollPosition(top);
    };

    window.addEventListener("scroll", onWindowScrolled);

    return () => {
      window.removeEventListener("scroll", onWindowScrolled);
    };
  }, [location]);

  return values;
};

export default useWindowScroll;
